import { Box, Typography } from '@mui/material';

import {
  InfoReviewFieldLabel,
  InfoReviewFieldValue,
} from '@/components/Form/Form.styled';

import { getFundingSourceOptions } from '@/constants/user';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/baseSelectors';
import {
  selectAccountTypeById,
  selectActiveUserAccountTypes,
  selectSameOperationIdAccountTypes,
  selectUser,
} from '@/redux/user-data/selectors';

import { UserAccountType } from '@/types/user';

import {
  getUserMonthlyDepositCurrency,
  isRemediationCustomer,
} from '@/utils/user';

import { ReviewCard } from './ReviewCard';
import { SectionProps } from './types';

const getAccountLabel = (account: UserAccountType) =>
  `(${account.feeCurrency || account.currency}) ${account.name}`;

const formatDepositAmount = (currency: string, amount?: number) =>
  amount != null ? `${currency} ${amount.toLocaleString('en-US')}` : '';

export function FundingSection({
  currentIndex,
  totalSections,
}: Readonly<SectionProps>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectUser);
  const accountTypeById = useAppSelector(selectAccountTypeById);
  const { defaultCurrency, tenant } = useAppSelector(selectConfigsState);
  const currentAccounts = useAppSelector(selectSameOperationIdAccountTypes);
  const remediationAccounts = useAppSelector(selectActiveUserAccountTypes);

  const isRemediation = isRemediationCustomer(user);

  const fundingSourceOptions = getFundingSourceOptions(tenant);
  const getIncomeTypeLabel = (fundingSource?: string) =>
    fundingSourceOptions.find(opt => opt.value === fundingSource)?.text ??
    fundingSource ??
    '';

  const accountsLabel = currentAccounts.map(getAccountLabel).join(', ');

  const depositCurrency = getUserMonthlyDepositCurrency(user, {
    accountTypeById,
    defaultCurrency,
  });

  const fundingEntries = isRemediation
    ? remediationAccounts.map(account => ({
        key: account.accountNo || account.id,
        heading: getAccountLabel(account),
        incomeType: getIncomeTypeLabel(account.fundingSource),
        deposit: formatDepositAmount(
          account.feeCurrency || account.currency,
          account.estimatedMonthlyDepositAmount,
        ),
      }))
    : [
        {
          key: 'user',
          heading: '',
          incomeType: getIncomeTypeLabel(user?.fundingSource),
          deposit: formatDepositAmount(depositCurrency, user?.monthlyDeposit),
        },
      ];

  return (
    <ReviewCard
      title={t('moreInfo.reviewMyInformation.funding.title')}
      subtitle={
        !isRemediation &&
        accountsLabel &&
        t('moreInfo.reviewMyInformation.funding.subtitle', {
          accounts: accountsLabel,
        })
      }
      currentIndex={currentIndex}
      totalSections={totalSections}
    >
      {fundingEntries.map(entry => (
        <Box key={entry.key}>
          {entry.heading && (
            <Typography variant="bodyB2Strong" mt={1.5} display="block">
              {entry.heading}
            </Typography>
          )}
          <InfoReviewFieldLabel>
            {t(`moreInfo.reviewMyInformation.funding.field.incomeType`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>{entry.incomeType}</InfoReviewFieldValue>

          <InfoReviewFieldLabel>
            {t(`moreInfo.reviewMyInformation.funding.field.monthlyDeposit`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>{entry.deposit}</InfoReviewFieldValue>
        </Box>
      ))}
    </ReviewCard>
  );
}
